import type { ColDef, IServerSideGetRowsParams, SetFilterValuesFuncParams } from 'ag-grid-community';
import type { GroupBy } from '@/serverSideTable/serverSideTable.constants';
import type { AnyProperty } from '@/utilities.types';
import { logError } from '@/utilities/logger';

export const cleanColumnId = (columnId: string | undefined): string | undefined =>
  columnId?.replace('ag-Grid-AutoColumn-', '');

/**
 * Get the group by payload from AG Grid parameters.
 */
export const getGroupByPayloadFromAgGrid = (
  params: IServerSideGetRowsParams,
  getColumnId: (initialColumnId: string) => string = (initialColumnId) => initialColumnId,
): GroupBy | undefined => {
  const { rowGroupCols, valueCols, groupKeys } = params.request;

  if (!rowGroupCols.length) {
    return;
  }

  const rowGroupColumnIds = rowGroupCols.map((col) => getColumnId(col.id));
  const allowedValueColumnIds = new Set(
    params.api
      .getColumnDefs()
      ?.filter((columnDef: ColDef) => columnDef.enableValue !== false)
      ?.filter((columnDef: ColDef) => !rowGroupColumnIds.includes(getColumnId(columnDef.colId!)))
      .map((columnDef: ColDef) => columnDef.colId),
  );

  return {
    rowGroupColumnIds,
    valueColumns: valueCols
      .filter((col) => allowedValueColumnIds.has(col.id))
      .map((col) => ({
        columnId: getColumnId(col.id),
        aggregationFunction: col.aggFunc,
      }))
      .uniqBy((item) => item.columnId),
    groupKeys,
  };
};

export const fetchAndSetValuesForSetColumnFilter = async <ColumnType = string>(
  params: SetFilterValuesFuncParams<AnyProperty, ColumnType>,
  fetchValues: () => Promise<ColumnType[]>,
) => {
  try {
    const values = await fetchValues();
    params.success(values);
  } catch (e) {
    logError(e as Error);
    params.success([]);
  }
};
